import { useCallback, useEffect, useState } from "react";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import { FormattedMessage, useIntl } from "react-intl";
import { InfoCircleOutlined } from "@ant-design/icons";

import colors from "../../../../colors";
import { legalEntityName } from "../type";
import { setAddressForm } from "../address";
import CustomerAlert from "../customerAlert";
import InputItem, {
  nationalIdPattern,
} from "../../../app/generic/form/inputItem";
import AddressRow from "../address/addressRow";
import CustomIcon from "../../../app/generic/customIcon";
import SelectItem from "../../../app/generic/form/selectItem";
import { getEntity } from "../../../../modules/crudOperations";
import { checkHttpStatus, get } from "../../../../modules/httpFunctions";
import { decodeCourtName, toLower } from "../../../../modules/dataFunctions";

//for tests - 08877661 25130382 08566330 49607847 00245909 62026453 01099159 28612191

const defaultRegisterTypes = [
  { code: "CommercialRegister", name: "" },
  { code: "TradeRegister", name: "" },
];

const defaultPlaceTypes = [
  { code: "RegionalCourt", name: "" },
  { code: "MunicipalCourt", name: "" },
];
const regionalCourt = "KS", //"Krajský soud v",
  municipalCourt = "MS"; //"Městský soud v";

const isCommercial = (type) => defaultRegisterTypes[0].code === type;

const search = (query, handleSetCompanyInfo) => {
  if (query && query.length >= 5) {
    getEntity("company/trade", query, undefined, handleSetCompanyInfo);
  }
};

const handleSearchDebounced = AwesomeDebouncePromise(search, 1000);

export const parseEntityForm = (values) => ({
  identificator: values.identificator,
  representedBy: values.representedBy,
  type: values.type,
  registeredAt:
    !values.type || values.type === legalEntityName
      ? {
          type: values.registeredAtType,
          municipal: values.municipal,
          place: values.registeredAtPlace,
          placeType: values.registeredAtPlaceType,
          section: values.registeredAtSection,
          liner: values.registeredAtLiner,
        }
      : undefined,
});

export const parseInitialEntityForm = (entity) => {
  const values = {};
  if (entity) {
    const registeredAt = entity.registeredAt || {};
    values.identificator = entity.identificator;
    values.representedBy = entity.representedBy;
    values.type = entity.type || legalEntityName;
    values.registeredAtType = registeredAt.type || defaultRegisterTypes[0].code;
    values.registeredAtPlaceType =
      registeredAt.placeType || defaultPlaceTypes[0].code;
    values.municipal = registeredAt.municipal;
    values.registeredAtPlace = registeredAt.place;
    values.registeredAtSection = registeredAt.section;
    values.registeredAtLiner = registeredAt.liner;
  }
  return values;
};

export const getDefaultEntityForm = () => ({
  type: legalEntityName,
  registeredAtType: defaultRegisterTypes[0].code,
  registeredAtPlaceType: defaultPlaceTypes[0].code,
});

const nationalIdRegex = RegExp(nationalIdPattern);

const setPerson = (values, person) => {
  if (person) {
    let name = toLower(person.jmeno),
      surname = toLower(person.prijmeni);
    const degree = person.titulPredJmenem || "",
      degreeAfter = person.titulZaJmenem || "";
    if (!name && surname) {
      const parts = surname.split(" ");
      if (parts.length > 0) {
        name = parts[0].trim();
        surname = surname.substring(surname.indexOf(" ") + 1);
      }
    }
    values.representedBy = `${degree ? degree + " " : ""}${name} ${surname}${
      degreeAfter ? " " + degreeAfter : ""
    }`;
    values.contactPersonFirstName = name;
    values.contactPersonLastName = surname;
  }
};

const Identificator = ({ form, entity, legalEntity, formItemLayout }) => {
  const intl = useIntl();

  const [disabled, setDisabled] = useState(true);

  const [commercial, setCommercial] = useState(
    entity && entity.registeredAt
      ? isCommercial(entity.registeredAt.type)
      : true
  );
  const handleTypeSelect = useCallback((type) => {
    setCommercial(isCommercial(type));
  }, []);

  const [showError, setShowError] = useState();
  const [companyName, setCompanyName] = useState();
  const handleSetPublicInfo = useCallback(
    (info, data) => {
      const values = {
        registeredAtSection: "",
        registeredAtLiner: "",
        registeredAtPlace: "",
        municipal: "",
      };

      if (
        info &&
        info.zaznamy &&
        info.zaznamy.length > 0 &&
        info.zaznamy[0].spisovaZnacka &&
        info.zaznamy[0].spisovaZnacka.length > 0
      ) {
        const result = info.zaznamy[0];

        if (result && result.statutarniOrgany) {
          let member = undefined;
          for (let i = 0; i < result.statutarniOrgany.length; i++) {
            const organ = result.statutarniOrgany[i];
            if (organ && !organ.datumVymazu) {
              for (let j = 0; j < organ.clenoveOrganu.length; j++) {
                const clen = organ.clenoveOrganu[j];
                if (clen && !clen.datumVymazu) {
                  member = clen;
                }
              }
            }
          }
          if (member) {
            let person = member.fyzickaOsoba;
            if (
              !person &&
              member.pravnickaOsoba &&
              member.pravnickaOsoba.zastoupeni
            ) {
              const z = member.pravnickaOsoba.zastoupeni.find(
                (p) => !p.datumVymazu
              );
              person = (z && z.fyzickaOsoba) || {};
            }
            setPerson(values, person);
          }
        }
        const court =
          result.spisovaZnacka.find((s) => !s.datumVymazu) ??
          result.spisovaZnacka.find((s) => s);
        if (court) {
          values.registeredAtSection = court.oddil;
          values.registeredAtLiner = court.vlozka && court.vlozka.toString();
          const isRegional = court.soud.includes(regionalCourt);
          values.registeredAtPlace = decodeCourtName(
            court.soud
              .replace(isRegional ? regionalCourt : municipalCourt, "")
              .trim()
          );
          values.registeredAtPlaceType =
            defaultPlaceTypes[isRegional ? 0 : 1].code;
        } else {
          setDisabled(false);
        }
        form.setFieldsValue(values);
      } else if (data) {
        const person =
          data &&
          data.zaznamy &&
          data.zaznamy.length > 0 &&
          data.zaznamy[0].angazovaneOsoby &&
          data.zaznamy[0].angazovaneOsoby[0];
        if (person) {
          setPerson(values, person);
          form.setFieldsValue(values);
          setDisabled(false);
        }
      }
    },
    [form]
  );
  const handleSetCompanyInfo = useCallback(
    (tradeData, companyId) => {
      setDisabled(true);
      const hasTradeData =
        tradeData && tradeData.zaznamy && tradeData.zaznamy.length > 0;
      (hasTradeData
        ? Promise.resolve({ data: tradeData })
        : get(`company/${companyId}`)
      ).then((res) => {
        const data = res.data;
        if (
          hasTradeData ||
          (checkHttpStatus(res) &&
            data &&
            data.ekonomickeSubjekty &&
            data.ekonomickeSubjekty.length > 0)
        ) {
          setShowError();
          const company = hasTradeData
            ? data.zaznamy[0]
            : {
                obchodniJmeno: data.ekonomickeSubjekty[0].obchodniJmeno,
                ico: data.ekonomickeSubjekty[0].ico,
                adresySubjektu: [data.ekonomickeSubjekty[0].sidlo],
                typSubjektu: data.ekonomickeSubjekty[0].dic ? "P" : "F",
              };
          setCompanyName(company.obchodniJmeno);
          const values = {};
          const isLegal = company.typSubjektu === "P";
          if (legalEntity) {
            const newCode = defaultRegisterTypes[isLegal ? 0 : 1].code;
            values.registeredAtType = newCode;
            handleTypeSelect(newCode);
            if (isLegal) {
              getEntity("company/public", company.ico, undefined, (res) =>
                handleSetPublicInfo(res, data)
              );
            } else if (
              company.adresySubjektu &&
              company.adresySubjektu.length > 0
            ) {
              values.municipal = `${
                (
                  company.adresySubjektu.find((a) => a.kodStatu === "CZ") ??
                  company.adresySubjektu[0]
                ).nazevObce
              }${
                company.zivnostenskyUrad ? ` (${company.zivnostenskyUrad})` : ""
              }`;
            }
          }
          if (!isLegal || !values.representedBy) {
            let person =
              company.osobaPodnikatel ??
              (company.angazovaneOsoby && company.angazovaneOsoby[0]);
            if (!person) {
              const parts = company.obchodniJmeno
                ?.split(/(\s+)/)
                .filter((e) => e.trim().length > 0);
              if (parts.length >= 2) {
                person = { jmeno: parts[0], prijmeni: parts[1] };
              }
            }
            setPerson(values, person);
          }
          if (company.adresySubjektu.length > 0) {
            const address =
              company.adresySubjektu.find((a) => a.kodStatu === "CZ") ??
              company.adresySubjektu[0];
            setAddressForm(form, {
              name: company.obchodniJmeno,
              street: address.nazevUlice || address.nazevCastiObce,
              streetNumber: `${address.cisloDomovni}${
                address.cisloOrientacni ? `/${address.cisloOrientacni}` : ""
              }`,
              city: `${address.nazevObce}${
                address.nazevUlice &&
                address.nazevCastiObce &&
                address.nazevObce !== address.nazevCastiObce
                  ? `-${address.nazevCastiObce}`
                  : ""
              }`,
              zip: address.psc?.toString() ?? address.pscTxt,
              country: address.kodStatu,
            });
          }
          form.setFieldsValue(values);
        } else {
          setCompanyName();
          setShowError(true);
        }
      });
    },
    [form, legalEntity, handleTypeSelect, handleSetPublicInfo]
  );
  const [companyId, setCompanyId] = useState(true);
  const handleIdentificatorChange = useCallback(
    ({ target }) => {
      setCompanyId(!nationalIdRegex.test(target.value));
      handleSearchDebounced(target.value, (data) =>
        handleSetCompanyInfo(data, target.value)
      );
    },
    [handleSetCompanyInfo]
  );

  const [registerTypes, setRegisterTypes] = useState(defaultRegisterTypes);
  useEffect(() => {
    setRegisterTypes(
      defaultRegisterTypes.map((m) => ({
        ...m,
        name: intl.formatMessage({
          id: `registration.registeredAt.type.${m.code
            .substring(0, 1)
            .toLowerCase()}${m.code.substring(1)}`,
          defaultMessage: m.code,
        }),
      }))
    );
  }, [intl]);

  const [placeTypes, setPlaceTypes] = useState(defaultPlaceTypes);
  useEffect(() => {
    setPlaceTypes(
      defaultPlaceTypes.map((m) => ({
        ...m,
        name: intl.formatMessage({
          id: `registration.registeredAt.${m.code
            .substring(0, 1)
            .toLowerCase()}${m.code.substring(1)}`,
          defaultMessage: m.code,
        }),
      }))
    );
  }, [intl]);

  return (
    <>
      <CustomerAlert
        show={showError}
        textId="registration.companyId.unknown"
        defautTextMessage="Vážený zákazníku, automatická registrace je povolena pouze pro subjekty se sídlem v České Republice. Pro registraci prosím Zákaznický servis DHL Freight CZ na telefonním čísle 239 018 222 nebo napište email na DHLFreight.CZ@dhl.com."
      />
      <InputItem
        required
        companyId
        allowClear
        czechCompanyId
        nationalId={!legalEntity}
        fieldName="identificator"
        formItemLayout={formItemLayout}
        onChange={handleIdentificatorChange}
        label={
          legalEntity ? (
            <FormattedMessage
              id="registration.companyId"
              defaultMessage="IČO"
            />
          ) : (
            <FormattedMessage
              id="registration.companyIdOrNationalId"
              defaultMessage="IČO / RČ"
            />
          )
        }
        addonAfter={
          companyName ? (
            <CustomIcon
              size={15}
              color={colors.GRAY}
              title={companyName}
              icon={InfoCircleOutlined}
            />
          ) : undefined
        }
      />
      <InputItem
        disabled={disabled}
        fieldName="representedBy"
        formItemLayout={formItemLayout}
        message={
          <FormattedMessage
            id="registration.representedBy.empty"
            defaultMessage="Vyplňte prosím jméno a přijmení zastupující osoby."
          />
        }
        label={
          <FormattedMessage
            id="registration.representedBy"
            defaultMessage="Zastoupený"
          />
        }
      />
      {legalEntity ? (
        <>
          <SelectItem
            required
            disabled={disabled}
            options={registerTypes}
            onSelect={handleTypeSelect}
            fieldName="registeredAtType"
            formItemLayout={formItemLayout}
            label={
              <FormattedMessage
                id="registration.registeredAt.type"
                defaultMessage="Zapsaný v"
              />
            }
          />
          {commercial ? (
            <>
              <InputItem
                required
                disabled={disabled}
                fieldName="registeredAtPlace"
                formItemLayout={formItemLayout}
                addonBefore={
                  <SelectItem
                    required
                    noStyle
                    allowClear={false}
                    options={placeTypes}
                    formItemLayout={formItemLayout}
                    fieldName="registeredAtPlaceType"
                  />
                }
              />
              <AddressRow
                equal
                firstChildren={
                  <InputItem
                    required
                    disabled={disabled}
                    fieldName="registeredAtSection"
                    formItemLayout={formItemLayout}
                    addonBefore={
                      <FormattedMessage
                        id="registration.registeredAt.section"
                        defaultMessage="oddíl"
                      />
                    }
                  />
                }
                secondChildren={
                  <InputItem
                    required
                    disabled={disabled}
                    fieldName="registeredAtLiner"
                    formItemLayout={formItemLayout}
                    addonBefore={
                      <FormattedMessage
                        id="registration.registeredAt.liner"
                        defaultMessage="vložka"
                      />
                    }
                  />
                }
              />
            </>
          ) : companyId ? (
            <InputItem
              required
              disabled={disabled}
              fieldName="municipal"
              formItemLayout={formItemLayout}
              addonBefore={
                <FormattedMessage
                  id="registration.municipal"
                  defaultMessage="U"
                />
              }
              placeholder={intl.formatMessage({
                id: "registration.municipal.placeholder",
                defaultMessage: "Magistrát města Ostrava",
              })}
            />
          ) : (
            <></>
          )}
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default Identificator;
